/**
 * Period Kit Result Page
 * 
 * Displays personalized recommendations based on quiz results
 * Uses Section Rendering API to fetch Liquid product cards
 * Shows individual cards per day (not grouped by quantity)
 */

class PeriodKitResult {
  constructor() {
    this.config = window.periodKitResultConfig || {};
    this.quizData = this.parseQueryParams();
    this.productCards = [];
    
    this.init();
  }

  getHeavyPantyProducts() {
    const list = this.config?.heavyPantyProducts;
    if (Array.isArray(list) && list.length) return list.filter(Boolean);
    return [];
  }

  getUltraPantyProducts() {
    const list = this.config?.ultraPantyProducts;
    if (Array.isArray(list) && list.length) return list.filter(Boolean);
    return [];
  }

  /**
   * Parse quiz data from URL query parameters
   */
  parseQueryParams() {
    const params = new URLSearchParams(window.location.search);
    
    let usageByDay = [];
    try {
      const usageStr = params.get('usageByDay');
      if (usageStr) {
        usageByDay = JSON.parse(decodeURIComponent(usageStr));
      }
    } catch (e) {
      console.error('Error parsing usageByDay:', e);
    }

    return {
      name: params.get('name') || '',
      cycleDays: parseInt(params.get('cycleDays')) || 0,
      productType: params.get('productType') || '',
      size: params.get('size') || '',
      style: params.get('style') || '',
      color: params.get('color') || '',
      usageByDay: usageByDay,
      email: params.get('email') || '',
      phone: params.get('phone') || ''
    };
  }

  /**
   * Initialize the result page
   */
  init() {
    // Validate we have required quiz data
    if (!this.validateQuizData()) {
      this.showError('Missing quiz data. Please <a href="/pages/build-your-period-kit">start the quiz again</a>.');
      return;
    }

    // Validate product configuration
    if (this.getHeavyPantyProducts().length === 0 && this.getUltraPantyProducts().length === 0) {
      this.showError('Products not configured. Please contact support.');
      console.error('No panty products configured in section settings');
      return;
    }

    // Update personalized greeting
    this.updateGreeting();

    // Generate individual recommendations (one per day)
    const recommendations = this.generateRecommendations();
    
    // Render product cards
    this.renderCards(recommendations);
    
    // Set up event handlers
    this.setupEventHandlers();
  }

  /**
   * Validate that all required quiz data is present
   */
  validateQuizData() {
    return this.quizData.name && 
           this.quizData.cycleDays > 0 && 
           this.quizData.productType &&
           this.quizData.size &&
           this.quizData.style &&
           this.quizData.color &&
           this.quizData.usageByDay.length > 0;
  }

  /**
   * Update the personalized greeting with customer name, size, and style
   */
  updateGreeting() {
    const nameSpan = document.getElementById('result-name');
    if (nameSpan && this.quizData.name) {
      nameSpan.textContent = this.quizData.name;
    }
    const metaEl = document.getElementById('result-meta');
    const sizeLine = document.getElementById('result-size-line');
    const styleLine = document.getElementById('result-style-line');
    if (sizeLine && this.quizData.size) {
      sizeLine.textContent = `Size - ${this.quizData.size}`;
      sizeLine.style.display = '';
    } else if (sizeLine) {
      sizeLine.style.display = 'none';
    }
    if (styleLine && this.quizData.style) {
      styleLine.textContent = `Style - ${this.quizData.style}`;
      styleLine.style.display = '';
    } else if (styleLine) {
      styleLine.style.display = 'none';
    }
    if (metaEl) {
      metaEl.style.display = (this.quizData.size || this.quizData.style) ? '' : 'none';
    }
  }

  /**
   * Generate individual product recommendations (one per day)
   * Algorithm: If quantity of pad/tampon/cup is 2 or less per 12 hour, select Heavy else suggest Ultra
   */
  generateRecommendations() {
    const recommendations = [];
    
    // Process each day's usage
    for (const dayUsage of this.quizData.usageByDay) {
      const day = dayUsage.day;
      const units = parseInt(dayUsage.units) || 0;
      
      // Algorithm: ≤2 per 12 hours = Heavy, >2 = Ultra
      const absorbency = units <= 2 ? 'Heavy' : 'Ultra';
      const products = absorbency === 'Heavy'
        ? this.getHeavyPantyProducts()
        : this.getUltraPantyProducts();

      if (!Array.isArray(products) || products.length === 0) continue;

      // Option B: Only ONE product per day.
      // Rotate through the configured list by day number (stable & predictable).
      const dayNum = parseInt(day, 10);
      const idx = Number.isFinite(dayNum) && dayNum > 0 ? (dayNum - 1) % products.length : 0;
      const product = products[idx] || products[0];
      if (!product) continue;

      const variant = this.findMatchingVariant(product);
      if (!variant) continue;

      recommendations.push({
        day,
        absorbency,
        product,
        variant,
        type: 'panty'
      });
    }
    
    // Add cream product (single card)
    if (this.config.creamProduct?.variants?.length) {
      const creamVariant = this.config.creamProduct.variants[0];
      recommendations.push({
        day: null,
        absorbency: null,
        product: this.config.creamProduct,
        variant: creamVariant,
        type: 'cream'
      });
    }
    
    return recommendations;
  }

  /**
   * Find a variant matching the quiz selections (Size, Style, Color)
   */
  findMatchingVariant(product) {
    if (!product || !product.variants || product.variants.length === 0) {
      return null;
    }
    
    const targetSize = this.normalizeValue(this.quizData.size);
    const targetStyle = this.normalizeValue(this.quizData.style);
    const targetColor = this.normalizeValue(this.quizData.color);
    
    // Get product options to understand which option is which
    const options = product.options || [];
    let sizeOptionIndex = -1;
    let styleOptionIndex = -1;
    let colorOptionIndex = -1;
    
    options.forEach((opt, index) => {
      const optName = this.normalizeValue(opt.name);
      if (optName.includes('size')) sizeOptionIndex = index;
      if (optName.includes('style')) styleOptionIndex = index;
      if (optName.includes('color') || optName.includes('colour')) colorOptionIndex = index;
    });
    
    // If a color was selected and the product has ANY available variant in that color,
    // restrict matching to that color so the recommendation reflects the chosen color.
    let restrictToColor = false;
    if (targetColor && colorOptionIndex !== -1) {
      restrictToColor = product.variants.some((variant) => {
        if (!variant?.available) return false;
        const vc = this.getVariantOption(variant, colorOptionIndex);
        return this.matchesColor(vc, targetColor);
      });
    }

    // Score-based matching
    let bestMatch = null;
    let bestScore = -1;
    
    for (const variant of product.variants) {
      if (!variant.available) continue;

      if (restrictToColor) {
        const vc = this.getVariantOption(variant, colorOptionIndex);
        if (!this.matchesColor(vc, targetColor)) continue;
      }
      
      let score = 0;
      
      const variantSize = this.getVariantOption(variant, sizeOptionIndex);
      if (this.normalizeValue(variantSize) === targetSize) {
        score += 10;
      }
      
      const variantStyle = this.getVariantOption(variant, styleOptionIndex);
      if (this.matchesStyle(variantStyle, targetStyle)) {
        score += 5;
      }
      
      const variantColor = this.getVariantOption(variant, colorOptionIndex);
      if (this.matchesColor(variantColor, targetColor)) {
        score += 3;
      }
      
      if (score > bestScore) {
        bestScore = score;
        bestMatch = variant;
      }
    }
    
    if (bestMatch && bestScore >= 10) {
      return bestMatch;
    }
    
    // Fallback to first available variant
    return product.variants.find(v => v.available) || product.variants[0];
  }

  getVariantOption(variant, index) {
    if (index === 0) return variant.option1;
    if (index === 1) return variant.option2;
    if (index === 2) return variant.option3;
    return null;
  }

  normalizeValue(value) {
    if (!value) return '';
    return String(value).toLowerCase().trim().replace(/-/g, ' ').replace(/\s+/g, ' ');
  }

  matchesStyle(variantStyle, targetStyle) {
    if (!variantStyle || !targetStyle) return false;
    const vs = this.normalizeValue(variantStyle);
    const ts = this.normalizeValue(targetStyle);
    if (vs === ts) return true;
    if (vs.includes(ts) || ts.includes(vs)) return true;
    const vsWords = vs.split(' ');
    const tsWords = ts.split(' ');
    return vsWords.filter(w => tsWords.includes(w)).length >= 2;
  }

  matchesColor(variantColor, targetColor) {
    if (!variantColor || !targetColor) return false;
    const vc = this.normalizeValue(variantColor);
    const tc = this.normalizeValue(targetColor);
    return vc === tc || vc.includes(tc) || tc.includes(vc);
  }

  /**
   * Format price using shop money format
   */
  formatMoney(cents) {
    const format = this.config.moneyFormat || '{{amount}}';
    const value = (cents / 100).toFixed(2);
    return format.replace(/\{\{\s*amount\s*\}\}/g, value);
  }

  /**
   * Strip HTML from formatMoney output - returns plain price text for ribbon display.
   * formatMoney can return HTML when shop.money_format contains <span class="money"> etc.
   */
  toPlainPrice(formatted) {
    if (!formatted || typeof formatted !== 'string') return formatted || '';
    const stripped = formatted.replace(/<[^>]+>/g, '').trim();
    const match = stripped.match(/(?:₹|Rs\.?|[\$€£])\s*[\d,]+\.?\d*|[\d,]+\.?\d*/);
    return (match ? match[0].trim() : stripped) || stripped;
  }

  /**
   * Render all product cards with day headings, checkboxes, separate cream section, sticky ribbon
   */
  renderCards(recommendations) {
    const pantyGrid = document.getElementById('panty-products-grid');
    const creamWrapper = document.getElementById('cream-product-wrapper');
    const stickyRibbon = document.getElementById('sticky-ribbon');
    const loadingEl = document.getElementById('result-loading');
    
    if (!pantyGrid) return;
    
    const pantyRecs = recommendations.filter(r => r.type === 'panty' && r.product && r.variant);
    const creamRec = recommendations.find(r => r.type === 'cream' && r.product && r.variant);
    
    this.productCards = recommendations.filter(rec => rec.product && rec.variant);
    
    if (this.productCards.length === 0) {
      if (loadingEl) loadingEl.remove();
      pantyGrid.innerHTML = `
        <div class="period-kit-result__error" style="grid-column: 1 / -1;">
          No products found matching your preferences. 
          <a href="/pages/build-your-period-kit">Please try the quiz again</a>.
        </div>
      `;
      return;
    }
    
    if (loadingEl) loadingEl.remove();
    
    // Render panty products grouped per day (cardIndex matches productCards order)
    let cardIndex = 0;
    const columns = [];
    for (let i = 0; i < pantyRecs.length; ) {
      const currentDay = pantyRecs[i]?.day;
      const group = [];
      while (i < pantyRecs.length && pantyRecs[i]?.day === currentDay) {
        group.push(pantyRecs[i]);
        i += 1;
      }
      columns.push(this.renderPantyDayColumn(group, cardIndex));
      cardIndex += group.length;
    }
    pantyGrid.innerHTML = columns.join('');
    
    // Render cream product in separate section
    if (creamWrapper && creamRec) {
      const creamCardIndex = this.productCards.length - 1;
      console.log('[PeriodKit] renderCards: creamCardIndex:', creamCardIndex, 'productCards.length:', this.productCards.length);
      creamWrapper.innerHTML = this.renderProductCard(creamRec, creamCardIndex, true);
    } else if (creamWrapper) {
      creamWrapper.innerHTML = '';
      const creamSection = document.getElementById('cream-section');
      if (creamSection) creamSection.style.display = 'none';
    }
    
    // Update sticky ribbon and show it
    this.updateStickyRibbon();
    if (stickyRibbon) stickyRibbon.classList.add('is-visible');
    // Re-apply after ticks so custom elements (product-card) are initialized and cream checkbox is detectable
    requestAnimationFrame(() => this.updateStickyRibbon());
    setTimeout(() => this.updateStickyRibbon(), 150);
  }

  /**
   * Render panty card with day heading and subheading
   * index = cardIndex in productCards (used for selection tracking)
   */
  renderPantyCardWithDay(recommendation, cardIndex) {
    const dayHeading = recommendation.day ? `DAY ${recommendation.day}` : '';
    const subheading = recommendation.day
      ? `Based on your flow on day ${recommendation.day} we recommend`
      : '';
    const cardHtml = this.renderProductCard(recommendation, cardIndex, false);
    return `
      <div class="period-kit-result__day-column">
        ${dayHeading ? `<h3 class="period-kit-result__day-heading">${dayHeading}</h3>` : ''}
        ${subheading ? `<p class="period-kit-result__day-subheading">${subheading}</p>` : ''}
        ${cardHtml}
      </div>
    `;
  }

  renderPantyDayColumn(recommendationsForDay, startCardIndex) {
    if (!Array.isArray(recommendationsForDay) || recommendationsForDay.length === 0) return '';
    const day = recommendationsForDay[0].day;
    const dayHeading = day ? `DAY ${day}` : '';
    const subheading = day ? `Based on your flow on day ${day} we recommend` : '';
    const cardsHtml = recommendationsForDay
      .map((rec, offset) => this.renderProductCard(rec, startCardIndex + offset, false))
      .join('');
    return `
      <div class="period-kit-result__day-column">
        ${dayHeading ? `<h3 class="period-kit-result__day-heading">${dayHeading}</h3>` : ''}
        ${subheading ? `<p class="period-kit-result__day-subheading">${subheading}</p>` : ''}
        ${cardsHtml}
      </div>
    `;
  }

  /**
   * Get selected card indices from the DOM (source of truth for display)
   * Each card has unique data-card-index; same variant can appear on multiple cards.
   * Scoped to section to avoid picking up other checkboxes; includes cream section.
   */
  getSelectedCardIndicesFromDOM() {
    const indices = new Set();
    const section = document.getElementById('period-kit-result');
    if (!section) {
      console.log('[PeriodKit] getSelectedCardIndicesFromDOM: section not found');
      return indices;
    }
    const allCheckboxes = section.querySelectorAll('.period-kit-result__card-checkbox');
    const checkboxes = section.querySelectorAll('.period-kit-result__card-checkbox.is-checked');
    console.log('[PeriodKit] getSelectedCardIndicesFromDOM: total checkboxes:', allCheckboxes.length, 'checked:', checkboxes.length);
    checkboxes.forEach((checkbox, i) => {
      const idx = checkbox.getAttribute('data-card-index');
      const productType = checkbox.getAttribute('data-product-type');
      const variantId = checkbox.getAttribute('data-variant-id');
      console.log('[PeriodKit] checkbox', i, 'data-card-index:', idx, 'data-product-type:', productType, 'data-variant-id:', variantId);
      if (idx !== null && idx !== '') {
        const parsed = parseInt(idx, 10);
        if (!isNaN(parsed) && parsed >= 0) indices.add(parsed);
      }
    });
    console.log('[PeriodKit] getSelectedCardIndicesFromDOM: selectedIndices:', [...indices]);
    return indices;
  }

  /**
   * Update sticky ribbon with total, compare price, discount
   * Uses DOM checkboxes as source of truth for selection state (per-card, not per-variant)
   */
  updateStickyRibbon() {
    const totalEl = document.getElementById('ribbon-total');
    const compareEl = document.getElementById('ribbon-compare');
    const discountEl = document.getElementById('ribbon-discount');
    
    if (!totalEl) return;
    
    const selectedIndices = this.getSelectedCardIndicesFromDOM();
    console.log('[PeriodKit] updateStickyRibbon: productCards.length:', this.productCards.length, 'productCards types:', this.productCards.map((c, i) => ({ i, type: c.type, variantId: c.variant?.id, available: c.variant?.available })));
    // Include cream when selected even if variant.available is false (Liquid/config can be wrong); panties still require available
    let availableCards = this.productCards.filter((c, index) => {
      if (!c.variant || !selectedIndices.has(index)) return false;
      if (c.type === 'cream') return true; // Cream: include when selected
      return c.variant.available !== false; // Panties: require available
    });
    console.log('[PeriodKit] updateStickyRibbon: availableCards after filter:', availableCards.length, 'cards:', availableCards.map(c => ({ type: c.type, price: c.variant?.price })));
    // Fallback: include cream if its checkbox is checked but wasn't found by index
    // Check both data-product-type and any checked checkbox inside cream wrapper (most reliable)
    const creamWrapper = document.getElementById('cream-product-wrapper');
    const creamCheckedByType = document.getElementById('period-kit-result')?.querySelector('.period-kit-result__card-checkbox.is-checked[data-product-type="cream"]');
    const creamCheckedInWrapper = creamWrapper?.querySelector('.period-kit-result__card-checkbox.is-checked');
    const creamChecked = creamCheckedByType || creamCheckedInWrapper;
    const creamCard = this.productCards.find((c) => c.type === 'cream');
    const creamWrapperCheckboxes = creamWrapper?.querySelectorAll('.period-kit-result__card-checkbox') || [];
    console.log('[PeriodKit] cream fallback: creamWrapper exists:', !!creamWrapper, 'creamCheckboxes in wrapper:', creamWrapperCheckboxes.length, 'creamCheckedByType:', !!creamCheckedByType, 'creamCheckedInWrapper:', !!creamCheckedInWrapper, 'creamCard:', !!creamCard, 'creamInAvailable:', availableCards.some((c) => c.type === 'cream'));
    creamWrapperCheckboxes.forEach((cb, i) => console.log('[PeriodKit] cream checkbox', i, 'classes:', cb.className, 'data-card-index:', cb.getAttribute('data-card-index')));
    if (creamChecked && creamCard && creamCard.variant && !availableCards.some((c) => c.type === 'cream')) {
      availableCards = [...availableCards, creamCard];
      console.log('[PeriodKit] cream added via fallback, cream price:', creamCard.variant.price);
    }
    let totalCents = 0;
    let compareTotalCents = 0;
    
    availableCards.forEach(c => {
      totalCents += c.variant.price;
      if (c.variant.compareAtPrice && c.variant.compareAtPrice > c.variant.price) {
        compareTotalCents += c.variant.compareAtPrice;
      } else {
        compareTotalCents += c.variant.price;
      }
    });
    console.log('[PeriodKit] updateStickyRibbon: totalCents:', totalCents, 'compareTotalCents:', compareTotalCents);
    
    const totalFormatted = this.formatMoney(totalCents);
    const compareFormatted = compareTotalCents > totalCents ? this.formatMoney(compareTotalCents) : '';
    const totalPlain = this.toPlainPrice(totalFormatted) || totalFormatted.replace(/<[^>]+>/g, '').trim();
    const comparePlain = this.toPlainPrice(compareFormatted) || (compareFormatted ? compareFormatted.replace(/<[^>]+>/g, '').trim() : '');
    
    this.setRibbonPrice(totalEl, totalPlain);
    if (compareEl) {
      if (compareTotalCents > totalCents) {
        this.setRibbonPrice(compareEl, comparePlain);
        compareEl.style.display = '';
      } else {
        compareEl.innerHTML = '';
        compareEl.style.display = 'none';
      }
    }
    
    this.rectifyRibbonPrices(totalEl, compareEl, totalPlain, comparePlain, compareTotalCents > totalCents);
    
    const addBtn = document.getElementById('add-full-kit');
    if (addBtn) {
      addBtn.disabled = availableCards.length === 0;
    }
    
    if (discountEl) {
      if (compareTotalCents > totalCents && compareTotalCents > 0) {
        const pct = Math.round((compareTotalCents - totalCents) / compareTotalCents * 100);
        // Hide badge when discount rounds to 0% (e.g. cream 15% off on 8 items = ~0.45% kit discount)
        if (pct > 0) {
          discountEl.textContent = `${pct}% OFF`;
          discountEl.style.display = '';
        } else {
          discountEl.style.display = 'none';
        }
      } else {
        discountEl.style.display = 'none';
      }
    }
  }

  /**
   * Set price in ribbon element - text only, no nested spans
   */
  setRibbonPrice(el, formatted) {
    if (!el) return;
    el.textContent = formatted;
  }

  /**
   * Extract price amount only from element content (handles nested spans, HTML as text from Liquid)
   */
  extractPriceFromElement(el) {
    if (!el) return '';
    const text = el.textContent || '';
    const stripped = text.replace(/<[^>]+>/g, '').trim();
    const match = stripped.match(/(?:₹|Rs\.?|[\$€£])\s*[\d,]+\.?\d*|[\d,]+\.?\d*/);
    return (match ? match[0].trim() : stripped) || text.trim();
  }

  /**
   * Replace element content with extracted price only - text only, no spans
   */
  setRibbonPriceToExtracted(el) {
    if (!el) return;
    const price = this.extractPriceFromElement(el);
    if (price) el.textContent = price;
  }

  /**
   * Check if content was messed up (span/HTML inserted as text) and rectify by extracting price only
   */
  rectifyRibbonPrices(totalEl, compareEl, totalPlain, comparePlain, hasCompare) {
    const rectify = () => {
      if (totalEl) {
        if (this.isRibbonContentMessedUp(totalEl)) {
          this.setRibbonPriceToExtracted(totalEl);
        } else {
          this.setRibbonPrice(totalEl, totalPlain);
        }
      }
      if (compareEl && hasCompare) {
        if (this.isRibbonContentMessedUp(compareEl)) {
          this.setRibbonPriceToExtracted(compareEl);
        } else {
          this.setRibbonPrice(compareEl, comparePlain);
        }
      }
    };
    rectify();
    requestAnimationFrame(rectify);
    setTimeout(rectify, 100);
    setTimeout(rectify, 300);
  }

  /**
   * Returns true if content has HTML tags as literal text or nested .money spans (from Liquid/theme)
   */
  isRibbonContentMessedUp(el) {
    if (!el) return false;
    const text = el.textContent || '';
    const hasHtmlAsText = text.includes('<') || text.includes('>');
    const hasMoneySpan = el.querySelector('.money');
    return hasHtmlAsText || !!hasMoneySpan;
  }

  /**
   * Render a single product card (collection-style, no add to cart, with checkbox)
   */
  renderProductCard(recommendation, index, isCream = false) {
    const { product, variant, absorbency, day, type } = recommendation;
    
    if (!product || !variant) {
      return '';
    }
    
    const baseUrl = product.url || '#';
    const productUrl = baseUrl === '#' ? baseUrl : `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}variant=${variant.id}`;
    // Image source: variant.image (from Liquid image_url) or product.featuredImage fallback
    const imageSrc = variant.image || product.featuredImage || '';
    const priceFormatted = this.formatMoney(variant.price);
    const compareAtPrice = variant.compareAtPrice;
    const showCompare = compareAtPrice && compareAtPrice > variant.price;
    const compareFormatted = showCompare ? this.formatMoney(compareAtPrice) : null;
    const discountPercent = showCompare && compareAtPrice > 0
      ? Math.round((compareAtPrice - variant.price) / compareAtPrice * 100)
      : 0;
    
    const options = [];
    if (variant.option1 && variant.option1 !== 'Default Title') options.push(variant.option1);
    if (variant.option2 && variant.option2 !== 'Default Title') options.push(variant.option2);
    if (variant.option3 && variant.option3 !== 'Default Title') options.push(variant.option3);
    const variantInfo = ''; // Removed variant options display per Figma
    
    const imageHtml = imageSrc
      ? `<img src="${imageSrc}" alt="${this.escapeHtml(product.title)}" loading="${index < 4 ? 'eager' : 'lazy'}" class="product-media__image" width="600" height="600" style="--focal-point: 50.0% 50.0%;">`
      : '<div class="product-media__placeholder" style="aspect-ratio:1;background:var(--color-background-secondary,#f5f5f5);"></div>';
    
    const saleBadgeHtml = showCompare
      ? `<div class="product-badges product-badges--top-left" style="--badge-border-radius: 100px;">
          <div class="product-badges__badge product-badges__badge--rectangle color-scheme-6">Sale</div>
        </div>`
      : '';
    
    const checkboxHtml = `
      <button type="button" class="period-kit-result__card-checkbox is-checked" data-card-index="${index}" data-variant-id="${variant.id}"${isCream ? ' data-product-type="cream"' : ''} aria-label="Deselect product" title="Click to deselect">
        <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 7L6 11L12 3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;
    
    const priceHtml = `
      <div ref="priceContainer">
        ${showCompare ? `
          <span role="group">
            <span class="visually-hidden">Sale price&nbsp;</span>
            <span class="price"><span class="money">${priceFormatted}</span></span>
          </span>
          <span role="group">
            <span class="visually-hidden">Regular price&nbsp;</span>
            <span class="compare-at-price"><span class="money">${compareFormatted}</span></span>
          </span>
          <span class="discount-percentage" aria-label="${discountPercent}% off">${discountPercent}% OFF</span>
        ` : `
          <span class="price"><span class="money">${priceFormatted}</span></span>
        `}
      </div>
    `;
    
    const cardContent = `
      <product-card class="product-card" data-product-id="${product.id}" data-variant-id="${variant.id}" data-product-transition="false">
        <a href="${productUrl}" class="product-card__link">
          <span class="visually-hidden">${this.escapeHtml(product.title)}</span>
        </a>
        <div class="product-card__content layout-panel-flex layout-panel-flex--column product-grid__card spacing-style border-style gap-style color-scheme-5" style="--border-width: 1px; --border-style: solid; --border-color: #0c2b821a; --border-radius: 32px; overflow: hidden; --flex-direction: column; --product-card-gap: 16px; --quick-add-display: none; --padding-block-end: 16px; --padding-inline-start: 0; --padding-inline-end: 0;">
          <div class="card-gallery card-gallery--badge-top-left spacing-style border-style period-kit-result__card-media-wrap" style="--border-width: 1px; --border-style: none; --gallery-aspect-ratio: 1.0; --padding: 0; position: relative;" data-product-id="${product.id}">
            ${checkboxHtml}
            <a class="contents" href="${productUrl}" aria-label="${this.escapeHtml(product.title)}">
              <div class="product-media" style="--ratio: 1.0">
                ${imageHtml}
              </div>
            </a>
            ${saleBadgeHtml}
          </div>
          <div class="product-grid-view-zoom-out--details">
            <h3 class="h4 period-kit-result__card-title">${this.escapeHtml(product.title)}</h3>
          </div>
          <a class="contents user-select-text" href="${productUrl}">
            <div class="text-block spacing-style period-kit-result__card-text" style="--color: var(--color-foreground); --text-align: left;">
              <p class="period-kit-result__card-title">${this.escapeHtml(product.title)}</p>
              ${variantInfo ? `<p style="font-size: 0.875rem; color: var(--color-foreground-75); margin: 4px 0 0;">${this.escapeHtml(variantInfo)}</p>` : ''}
            </div>
          </a>
          <product-price class="text-block text-block--align-left text-left h6 spacing-style period-kit-result__card-price" style="--color: var(--color-foreground); --width: 100%;" data-product-id="${product.id}">
            ${priceHtml}
          </product-price>
        </div>
      </product-card>
    `;
    
    return isCream ? cardContent : cardContent;
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Set up event handlers (no per-card add to cart - only Add Full Kit)
   */
  setupEventHandlers() {
    // Add full kit button
    document.getElementById('add-full-kit')?.addEventListener('click', () => this.addFullKitToCart());

    // Reset kit button
    document.getElementById('reset-kit')?.addEventListener('click', () => {
      sessionStorage.clear();
      window.location.href = '/pages/build-your-period-kit';
    });

    // Checkbox click: toggle product selection (per-card)
    document.getElementById('period-kit-result')?.addEventListener('click', (e) => {
      const checkbox = e.target.closest('.period-kit-result__card-checkbox');
      if (!checkbox) return;
      e.preventDefault();
      e.stopPropagation();
      if (checkbox.classList.contains('is-checked')) {
        checkbox.classList.remove('is-checked');
        checkbox.setAttribute('aria-label', 'Select product');
        checkbox.setAttribute('title', 'Click to select');
      } else {
        checkbox.classList.add('is-checked');
        checkbox.setAttribute('aria-label', 'Deselect product');
        checkbox.setAttribute('title', 'Click to deselect');
      }
      this.updateStickyRibbon();
    });
  }

  getCartSectionIdsForAjaxUpdate() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      const id = el?.dataset?.sectionId;
      if (id) ids.add(id);
    });
    return Array.from(ids);
  }

  dispatchCartUpdateEvent(cartData, { itemCount = 0, variantId = '' } = {}) {
    // Horizon theme listens for `cart:update` (CartAddEvent.eventName) on document
    // and uses sections HTML (if present) to refresh cart drawer contents.
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cartData,
          sourceId: 'period-kit-result',
          data: {
            didError: false,
            source: 'period-kit-result',
            itemCount,
            variantId: variantId ? String(variantId) : '',
            sections: cartData?.sections || {},
          },
        },
      })
    );
  }

  /**
   * Add a single product variant to cart
   */
  async addToCart(variantId, quantity) {
    const body = new URLSearchParams();
    body.set('id', String(parseInt(variantId, 10)));
    body.set('quantity', String(quantity || 1));

    const sectionIds = this.getCartSectionIdsForAjaxUpdate();
    if (sectionIds.length) {
      body.set('sections', sectionIds.join(','));
      body.set('sections_url', window.location.pathname);
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: body.toString(),
    });

    const data = await response.json();
    if (!response.ok || data?.status) {
      throw new Error(data?.description || data?.message || 'Failed to add to cart');
    }

    this.updateCartCount();
    this.showSuccessMessage('Product added to cart!');
    this.dispatchCartUpdateEvent(data, { itemCount: Number(quantity || 1), variantId });
    return data;
  }

  /**
   * Add all products in the kit to cart
   */
  async addFullKitToCart() {
    const btn = document.getElementById('add-full-kit');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Adding Kit...';
    }

    try {
      // Collect selected cards from DOM (per-card selection)
      const selectedIndices = this.getSelectedCardIndicesFromDOM();
      // Include cream when selected even if variant.available is false; panties require available
      let selectedCards = this.productCards.filter((card, index) => {
        if (!card.variant || !selectedIndices.has(index)) return false;
        if (card.type === 'cream') return true;
        return card.variant.available !== false;
      });
      // Fallback: include cream if its checkbox is checked but wasn't found by index
      const creamWrapper = document.getElementById('cream-product-wrapper');
      const creamChecked = document.getElementById('period-kit-result')?.querySelector('.period-kit-result__card-checkbox.is-checked[data-product-type="cream"]') ||
        (creamWrapper?.querySelector('.period-kit-result__card-checkbox.is-checked'));
      const creamCard = this.productCards.find(c => c.type === 'cream');
      if (creamChecked && creamCard && creamCard.variant && !selectedCards.some(c => c.type === 'cream')) {
        selectedCards = [...selectedCards, creamCard];
      }
      const items = selectedCards.map(card => ({
        id: parseInt(card.variant.id),
        quantity: 1
      }));

      if (items.length === 0) {
        throw new Error('No available products to add');
      }

      const body = new URLSearchParams();
      items.forEach((item, i) => {
        body.set(`items[${i}][id]`, String(item.id));
        body.set(`items[${i}][quantity]`, String(item.quantity || 1));
      });

      const sectionIds = this.getCartSectionIdsForAjaxUpdate();
      if (sectionIds.length) {
        body.set('sections', sectionIds.join(','));
        body.set('sections_url', window.location.pathname);
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: body.toString(),
      });

      const data = await response.json();
      if (!response.ok || data?.status) {
        throw new Error(data?.description || data?.message || 'Failed to add kit to cart');
      }

      this.updateCartCount();
      this.showSuccessMessage(`${items.length} items added to cart!`);
      this.dispatchCartUpdateEvent(data, { itemCount: items.length, variantId: items[0]?.id });

      if (btn) {
        btn.textContent = 'Added!';
        setTimeout(() => {
          btn.textContent = 'Add to Cart';
          btn.disabled = false;
        }, 2000);
      }

    } catch (error) {
      console.error('Error adding full kit:', error);
      alert('Failed to add kit to cart. Please try again.');
      
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Add to Cart';
      }
    }
  }

  /**
   * Update the cart count in the header
   */
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cart = await response.json();
        document.querySelectorAll('[data-cart-count], .cart-count-bubble').forEach(el => {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? '' : 'none';
        });
      }
    } catch (e) {
      console.error('Error updating cart count:', e);
    }
  }

  /**
   * Show a success toast message
   */
  showSuccessMessage(message) {
    const toast = document.createElement('div');
    toast.className = 'period-kit-result__toast';
    toast.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z" fill="currentColor"/>
      </svg>
      <span>${message}</span>
    `;
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #10B981;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      display: flex;
      align-items: center;
      gap: 12px;
      animation: slideIn 0.3s ease;
    `;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.style.animation = 'slideOut 0.3s ease forwards';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  /**
   * Show an error message
   */
  showError(message) {
    const pantyGrid = document.getElementById('panty-products-grid');
    const creamSection = document.getElementById('cream-section');
    const stickyRibbon = document.getElementById('sticky-ribbon');
    if (pantyGrid) {
      const loading = document.getElementById('result-loading');
      if (loading) loading.remove();
      pantyGrid.innerHTML = `
        <div class="period-kit-result__error" style="grid-column: 1 / -1;">
          <h2 class="period-kit-result__error-title">Oops! Something's Missing</h2>
          <p class="period-kit-result__error-message">We couldn't find your quiz responses. Let's start fresh and build your perfect period kit!</p>
          <a href="/pages/build-your-period-kit" class="period-kit-result__error-link">Start the Quiz</a>
        </div>
      `;
    }
    if (creamSection) creamSection.style.display = 'none';
    if (stickyRibbon) stickyRibbon.classList.remove('is-visible');
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new PeriodKitResult());
} else {
  new PeriodKitResult();
}
